
import React from "react";
import * as echarts from 'echarts';
import "./Energy-generation.css";

/**
 * Energy_generation component renders a customizable Sankey chart using ECharts.
 * @param {Object} props - Props for customization (data, width, height, title, etc.)
 */



const Energy_generation = ({
  data,
  width = "100%",
  height = "100%",
  title = "",
  nodeAlign = "justify",
  nodeWidth = 20,
  nodeGap = 8,
  labelFontSize = 12,
  labelColor = "#333",
  linkColor = "source",
  ...rest
}) => {
  const sankeyNodes = (data?.nodes || []).map((node, index, nodes) => {
    if (index === nodes.length - 1) {
      return {
        ...node,
        label: {
          ...(node.label || {}),
          position: "left",
        },
      };
    }
    return node;
  });

  const option = {
    title: {
      text: '', // Remove ECharts title, use custom div
    },
    tooltip: {
      trigger: "item",
      formatter: (params) => {
        if (params.value !== undefined) {
          return `${params.name}: <b>${params.value.toFixed(2)}</b>`;
        }
        return params.name;
      },
      appendToBody: true,
      confine: false,
      backgroundColor: 'white',
      borderColor: '#ccc',
      borderWidth: 1,
      extraCssText: 'z-index: 9999;',
      textStyle: { color: '#333', fontSize: 14 },
    },
    series: [
      {
        type: "sankey",
        data: sankeyNodes,
        links: data.links,
        emphasis: { focus: "adjacency" },
        nodeAlign,
        nodeWidth,
        nodeGap,
        label: {
          show: true,
          color: labelColor,
          fontSize: labelFontSize,
        },
        lineStyle: {
          color: linkColor,
          curveness: 0.5,
        },
      },
    ],
  };

  // Custom hook to initialize and update the chart
  const chartRef = React.useRef(null);

  React.useEffect(() => {
    let chartInstance;
    let resizeObserver;
    let resizeTimeout;

    if (chartRef.current) {
      chartInstance = echarts.init(chartRef.current);
      chartInstance.setOption(option);

      // Debounced resize function to prevent loops
      const debouncedResize = () => {
        if (resizeTimeout) clearTimeout(resizeTimeout);
        resizeTimeout = setTimeout(() => {
          if (chartInstance && !chartInstance.isDisposed()) {
            try {
              chartInstance.resize();
            } catch (error) {
              console.warn('Chart resize error:', error);
            }
          }
        }, 100);
      };

      // Add resize observer with error handling
      try {
        resizeObserver = new ResizeObserver((entries) => {
          // Prevent infinite loops by checking if size actually changed
          for (let entry of entries) {
            const { width, height } = entry.contentRect;
            if (width > 0 && height > 0) {
              debouncedResize();
            }
          }
        });

        resizeObserver.observe(chartRef.current);
      } catch (error) {
        console.warn('ResizeObserver error:', error);
      }

      // Also listen for window resize with debouncing
      const handleWindowResize = () => {
        debouncedResize();
      };

      window.addEventListener('resize', handleWindowResize);

      return () => {
        if (resizeTimeout) clearTimeout(resizeTimeout);
        window.removeEventListener('resize', handleWindowResize);
        if (resizeObserver) {
          try {
            resizeObserver.disconnect();
          } catch (error) {
            console.warn('ResizeObserver disconnect error:', error);
          }
        }
        if (chartInstance && !chartInstance.isDisposed()) {
          chartInstance.dispose();
        }
      };
    }
  }, [JSON.stringify(option)]);

  return (
    <div className="energy-generation-container" style={{ width, height, minHeight: '300px' }}>
      <div className="energy-generation-title">{title}</div>
      <div
        ref={chartRef}
        className="energy-generation-chart"
        style={{ width: '100%', height: 'calc(100% - 30px)' }}
        {...rest}
      />
    </div>
  );
};

export default Energy_generation;
